import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { offersService } from '../../../services/supabaseService';
import { trackEvent } from '../../../hooks/useGoogleAnalytics';
import Select from '../../../components/ui/Select';
import RequestCard from '../../traveler-dashboard/components/RequestCard';
import MyOffersPanel from '../../traveler-dashboard/components/MyOffersPanel';
import EarningsSummary from '../../traveler-dashboard/components/EarningsSummary';
import SubmitOfferModal from '../../traveler-dashboard/components/SubmitOfferModal';
import MyTripsSection from './MyTripsSection';
import Icon from '../../../components/AppIcon';

const TravelerMode = ({ data, onRefresh, currentLanguage }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filters, setFilters] = useState({ destination: '', category: '', budgetRange: '' });
  const [selectedRequest, setSelectedRequest] = useState(null);

  const handleSubmitOffer = (requestId) => {
    const request = data?.requests?.find(r => r?.id === requestId);
    if (request) {
      setSelectedRequest(request);
    }
  };

  const handleOfferSubmit = async (offerData) => {
    try {
      const newOffer = {
        requestId: selectedRequest?.id,
        travelerId: user?.id,
        proposedFee: offerData?.proposedFee,
        message: offerData?.message,
        status: 'pending'
      };

      const created = await offersService?.create(newOffer);
      if (created) {
        trackEvent('offer_submission', {
          request_id: selectedRequest?.id,
          traveler_id: user?.id,
          proposed_fee: offerData?.proposedFee,
          item_category: selectedRequest?.itemCategory,
          page: '/unified-dashboard'
        });
        onRefresh();
      }
    } catch (error) {
      console.error('Create offer error:', error);
    }
  };

  const handleViewDeal = async (offerId) => {
    try {
      const deal = data?.deals?.find(d => d?.offerId === offerId);
      if (deal) {
        navigate('/deal-room-page', { state: { dealId: deal?.id } });
      }
    } catch (error) {
      console.error('View deal error:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Calculate matching info for requests based on trips
  const requestsWithMatching = data?.requests?.map(request => {
    const matchingTrips = data?.trips?.filter(trip => {
      const routeMatch = trip?.fromCountry === request?.originCountry && 
                        trip?.toCountry === request?.destinationCountry;
      const categoryMatch = trip?.allowedCategories?.includes(request?.category);
      const dateMatch = new Date(request?.deliveryDate) >= new Date(trip?.departureDate) &&
                       new Date(request?.deliveryDate) <= new Date(trip?.returnDate);
      return routeMatch && categoryMatch && dateMatch;
    });

    return {
      ...request,
      matchingTrips: matchingTrips?.length,
      hasMatch: matchingTrips?.length > 0
    };
  });

  const filteredRequests = requestsWithMatching?.filter((request) => {
    if (filters?.destination && request?.destinationCountry !== filters?.destination) return false;
    if (filters?.category && request?.category !== filters?.category) return false;
    if (filters?.budgetRange) {
      const budget = request?.budget;
      if (filters?.budgetRange === 'low' && budget >= 100000) return false;
      if (filters?.budgetRange === 'medium' && (budget < 100000 || budget >= 500000)) return false;
      if (filters?.budgetRange === 'high' && budget < 500000) return false;
    }
    return true;
  });

  const translations = {
    en: {
      availableRequests: 'Available Purchase Requests',
      filterByDestination: 'Filter by Destination',
      filterByCategory: 'Filter by Category',
      filterByBudget: 'Filter by Budget',
      allDestinations: 'All Destinations',
      allCategories: 'All Categories',
      allBudgets: 'All Budgets',
      lowBudget: 'Low (< 100K)',
      mediumBudget: 'Medium (100K - 500K)',
      highBudget: 'High (> 500K)',
      noRequests: 'No purchase requests available',
      checkBack: 'Check back later for new opportunities',
      matchingTrips: 'matching trips'
    },
    ko: {
      availableRequests: '이용 가능한 구매 요청',
      filterByDestination: '목적지별 필터',
      filterByCategory: '카테고리별 필터',
      filterByBudget: '예산별 필터',
      allDestinations: '모든 목적지',
      allCategories: '모든 카테고리',
      allBudgets: '모든 예산',
      lowBudget: '낮음 (< 100K)',
      mediumBudget: '중간 (100K - 500K)',
      highBudget: '높음 (> 500K)',
      noRequests: '이용 가능한 구매 요청 없음',
      checkBack: '나중에 새로운 기회를 확인하세요',
      matchingTrips: '일치하는 여행'
    }
  };

  const t = translations?.[currentLanguage];

  const destinationOptions = [
    { value: '', label: t?.allDestinations },
    { value: 'South Korea', label: 'South Korea' },
    { value: 'USA', label: 'USA' },
    { value: 'Japan', label: 'Japan' },
    { value: 'France', label: 'France' },
    { value: 'UK', label: 'UK' }
  ];

  const categoryOptions = [
    { value: '', label: t?.allCategories },
    { value: 'electronics', label: 'Electronics' },
    { value: 'fashion', label: 'Fashion' },
    { value: 'cosmetics', label: 'Cosmetics' },
    { value: 'food', label: 'Food' },
    { value: 'books', label: 'Books' },
    { value: 'sports', label: 'Sports' },
    { value: 'toys', label: 'Toys' },
    { value: 'health', label: 'Health' }
  ];

  const budgetOptions = [
    { value: '', label: t?.allBudgets },
    { value: 'low', label: t?.lowBudget },
    { value: 'medium', label: t?.mediumBudget },
    { value: 'high', label: t?.highBudget }
  ];

  return (
    <div className="space-y-6 md:space-y-8">
      <EarningsSummary deals={data?.deals} currentLanguage={currentLanguage} />
      
      <MyTripsSection 
        trips={data?.trips} 
        requests={data?.requests}
        onRefresh={onRefresh}
        currentLanguage={currentLanguage} 
      />

      <div className="bg-card rounded-lg shadow-elevation-md p-4 md:p-6">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-4">
          {t?.availableRequests}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Select
            options={destinationOptions}
            value={filters?.destination}
            onChange={(value) => handleFilterChange('destination', value)}
            placeholder={t?.filterByDestination}
          />
          <Select
            options={categoryOptions}
            value={filters?.category}
            onChange={(value) => handleFilterChange('category', value)}
            placeholder={t?.filterByCategory}
          />
          <Select
            options={budgetOptions}
            value={filters?.budgetRange}
            onChange={(value) => handleFilterChange('budgetRange', value)}
            placeholder={t?.filterByBudget}
          />
        </div>

        {filteredRequests?.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-lg text-muted-foreground">{t?.noRequests}</p>
            <p className="text-sm text-muted-foreground mt-2">{t?.checkBack}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRequests?.map((request) => (
              <div key={request?.id} className="relative">
                {request?.hasMatch && (
                  <div className="absolute -top-2 -right-2 z-10 bg-success text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                    <Icon name="CheckCircle" size={14} />
                    {request?.matchingTrips} {t?.matchingTrips}
                  </div>
                )}
                <RequestCard
                  request={request}
                  onSubmitOffer={handleSubmitOffer}
                  currentLanguage={currentLanguage}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <MyOffersPanel
        offers={data?.offers}
        onViewDeal={handleViewDeal}
        currentLanguage={currentLanguage}
      />

      {selectedRequest && (
        <SubmitOfferModal
          request={selectedRequest}
          onSubmit={handleOfferSubmit}
          onClose={() => setSelectedRequest(null)}
          currentLanguage={currentLanguage}
        />
      )}
    </div>
  );
};

export default TravelerMode;